// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';
import dayjs from 'dayjs';

import { css } from '@emotion/css';
import { Theme, Typography, useTheme } from '@mui/material';

import { getGapBetweenTimes } from '@components/Timeline/Timeline/utils';

import { useTimeline } from '@components/Timeline/Timeline/TimelineProvider';

// TYPES ---------------------------------------------------------------- //

interface TimelineAreaMarkerProps {
  showLabel: boolean;
  marker: {
    time: string;
    label: string;
    color?: string;
  };
}

// COMPONENT ---------------------------------------------------------------- //

const TimelineAreaMarker = ({ showLabel, marker }: TimelineAreaMarkerProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const { date, timezoneOverride, zoom } = useTimeline();

  return (
    <div
      className={cls.timelineAreaMarker}
      style={{
        left:
          getGapBetweenTimes(
            dayjs(date).add(12, 'hours').tz(timezoneOverride).startOf('day').format(),
            dayjs(marker?.time).tz(timezoneOverride).format(),
            zoom
          ) || 0,
      }}
    >
      <div className={cls.line} style={{ borderLeftColor: marker?.color || theme.palette.text.primary }} />

      {showLabel ? (
        <div className={cls.label} style={{ backgroundColor: marker?.color || theme.palette.text.primary }}>
          <Typography sx={clsx.labelTxt}>{dayjs(marker?.time).tz(timezoneOverride).format(`h:mm A z`)}</Typography>
          <Typography sx={clsx.labelTxt}>{marker?.label}</Typography>
        </div>
      ) : null}
    </div>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  timelineAreaMarker: css`
    z-index: 401;
    position: absolute;
    display: flex;
    top: 0;
    height: 100%;
    user-select: none;
    pointer-events: none;
  `,

  line: css`
    border-left: 1px solid;
    height: 100%;
    box-shadow: 0px 0px 1px #ffffff;
  `,

  label: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    height: fit-content;
    padding: 3px;
    border-radius: 0 0 4px 0;
  `,
});

const useSxStyles = (theme: Theme) => ({
  labelTxt: {
    whiteSpace: 'nowrap',
    lineHeight: 1,
    fontSize: '10px',
    fontWeight: 500,
    color: theme.palette.text.contrastPrimary,
  },
});

// -------------------------------- EXPORT -------------------------------- //

export default TimelineAreaMarker;
